var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { OutlineFilter } from "@pixi/filter-outline";
import "pixi.js";
import { bindActionCreators } from "redux";
import { Subject } from "rxjs";
import { IdTooltipsDelay } from "../../../constants/IdTooltipsDelay";
import { IdTooltipType } from "../../../constants/IdTooltipType";
import { eventsCanvas } from "../../../events/eventsCanvas";
import { GetLockedGroundGraphicIso as BF2GetLockedGroundGraphicIso } from "../../../graphics/GetLockedGroundGraphicIso";
import { GetUnlockableGroundGraphicIso as BF2GetUnlockableGroundGraphicIso } from "../../../graphics/GetUnlockableGroundGraphicIso";
import { selectActiveExpansionDiscount, selectExpansionInfo, selectHighlightedExpansionId, } from "../../../selectors/selectExpansionInfo";
import { selectUnlockableExpansionIds } from "../../../selectors/selectUnlockableExpansionIds";
import { utilGrid } from "../../../utils/grid/utilGrid";
import { IdLoca } from "../../../utils/i18n/IdLoca";
import { shallowDiffers } from "../../../utils/shallowDiffers";
import { IdEventSoundCore } from "../../../utils/sound/IdEventSoundCore";
import { utilSound } from "../../../utils/sound/utilSound";
import { SceneLayer } from "../../scenes/SceneLayer";
import { actionTooltipHideBuildingText, actionTooltipShowBuildingText, } from "../../tooltip/actionTooltip";
import { TypeBuildingAction } from "../buildings/reducerBuildings";
import { actionExpansionsSelect } from "../expansions/actionExpansions";
var TypeLockedGround;
(function (TypeLockedGround) {
    TypeLockedGround[TypeLockedGround["Locked"] = 0] = "Locked";
    TypeLockedGround[TypeLockedGround["Unlockable"] = 1] = "Unlockable";
})(TypeLockedGround || (TypeLockedGround = {}));
var LayerPlayerBaseLockedGround = /** @class */ (function (_super) {
    __extends(LayerPlayerBaseLockedGround, _super);
    function LayerPlayerBaseLockedGround() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.update$ = new Subject();
        _this.lockedGroundById = new Map();
        _this.unlockableGroundById = new Map();
        _this.clickSubscriptionsByExpansionId = new Map();
        _this.mouseOverSubscriptionsByExpansionId = new Map();
        _this.mouseOutSubscriptionsByExpansionId = new Map();
        _this.previousHighlightedExpansionId = -1;
        _this.shouldFullUpdate = false;
        _this.shouldIsoSortUpdate = true;
        _this.shouldUpdate = function (state, previousState) {
            var expansions = state.expansionInfo.unlockedExpansionIds;
            var prevExpansions = previousState
                ? previousState.expansionInfo.unlockedExpansionIds
                : undefined;
            _this.shouldFullUpdate =
                !expansions ||
                    !prevExpansions ||
                    expansions.length !== prevExpansions.length ||
                    shallowDiffers(state.activeExpansionDiscount, previousState.activeExpansionDiscount);
            _this.previousHighlightedExpansionId = previousState
                ? previousState.highlightedExpansionId
                : -1;
            var hasHighlightChanged = _this.previousHighlightedExpansionId !== state.highlightedExpansionId;
            return _this.shouldFullUpdate || hasHighlightChanged;
        };
        _this.update = function (props) {
            if (_this.shouldFullUpdate) {
                _this.fullUpdate(props);
            }
            else {
                _this.partialUpdate(props);
            }
            if (_this.shouldIsoSortUpdate) {
                _this.update$.next(true);
            }
            else {
                _this.shouldIsoSortUpdate = true;
            }
        };
        _this.mapState = function (state) { return ({
            activeExpansionDiscount: selectActiveExpansionDiscount(state),
            buildingPlacement: state.playerBase.buildings,
            expansionInfo: selectExpansionInfo(state),
            getLockedGroundGraphicIso: BF2GetLockedGroundGraphicIso,
            getUnlockableGroundGraphicIso: BF2GetUnlockableGroundGraphicIso,
            highlightedExpansionId: selectHighlightedExpansionId(state),
            unlockableExpansionIds: selectUnlockableExpansionIds(state),
        }); };
        _this.mapDispatch = function (dispatch) {
            return bindActionCreators({
                onBuyExpansionClick: actionExpansionsSelect,
                onMouseOutDispatch: actionTooltipHideBuildingText,
                onMouseOverDispatch: actionTooltipShowBuildingText,
            }, dispatch);
        };
        _this.getIsoObject = function (ground) {
            var p = utilGrid.expansionIdToXY(ground.id, _this.baseSetup);
            // We're resizing and repositioning the locked ground isometric object
            // so that it actually has a margin of 1 iso tile around it.
            // This ensures there are no collisions between these and fence parts.
            return {
                display: _this.unlockableGroundById.get(ground.id) || _this.lockedGroundById.get(ground.id),
                id: ground.id,
                volume: 5,
                x: p.x + 1,
                xExtent: _this.baseSetup.expansionsSizeX - 2,
                y: p.y + 1,
                yExtent: _this.baseSetup.expansionsSizeY - 2,
            };
        };
        _this.getObjects = function () { return _this.lockedGrounds || []; };
        _this.fullUpdate = function (props) {
            var expansionInfo = props.expansionInfo, getLockedGroundGraphicIso = props.getLockedGroundGraphicIso, getUnlockableGroundGraphicIso = props.getUnlockableGroundGraphicIso, highlightedExpansionId = props.highlightedExpansionId, unlockableExpansionIds = props.unlockableExpansionIds;
            var unlockedExpansionIds = expansionInfo.unlockedExpansionIds;
            _this.baseSetup = expansionInfo.baseSetup;
            var unlockedOrUnlockables = unlockableExpansionIds.concat(unlockedExpansionIds);
            var drawList = [];
            // Remove unlocked ones
            unlockedOrUnlockables.forEach(function (id) {
                return _this.cleanupExpansion(_this.lockedGroundById.get(id) || _this.unlockableGroundById.get(id));
            });
            // Add Locked ones
            expansionInfo.expansions.forEach(function (expansion, id) {
                if (unlockedOrUnlockables.indexOf(id) === -1) {
                    drawList.push({
                        id: id,
                        type: TypeLockedGround.Locked,
                    });
                }
            });
            // Add unlockable ones
            unlockableExpansionIds.forEach(function (id) {
                drawList.push({
                    id: id,
                    type: TypeLockedGround.Unlockable,
                });
            });
            // Draw everything
            drawList.forEach(function (item) {
                if (item.type === TypeLockedGround.Locked) {
                    _this.drawObject(item, _this.baseSetup, _this.lockedGroundById, getLockedGroundGraphicIso);
                }
                else {
                    var display = _this.drawObject(item, _this.baseSetup, _this.unlockableGroundById, getUnlockableGroundGraphicIso);
                    if (display) {
                        _this.createSubscriptions(display, item, props);
                        if (highlightedExpansionId === item.id) {
                            _this.addHighlight(display);
                        }
                    }
                }
            });
            _this.lockedGrounds = drawList;
        };
        _this.partialUpdate = function (props) {
            var highlightedExpansionId = props.highlightedExpansionId;
            var previousDisplay = _this.unlockableGroundById.get(_this.previousHighlightedExpansionId);
            if (previousDisplay) {
                _this.removeHighlight(previousDisplay);
            }
            if (highlightedExpansionId !== -1) {
                var display = _this.unlockableGroundById.get(highlightedExpansionId);
                if (display) {
                    _this.addHighlight(display);
                }
            }
        };
        _this.createSubscriptions = function (display, item, props) {
            var expansionInfo = props.expansionInfo, onBuyExpansionClick = props.onBuyExpansionClick, onMouseOverDispatch = props.onMouseOverDispatch, onMouseOutDispatch = props.onMouseOutDispatch;
            var unlockedExpansionIds = expansionInfo.unlockedExpansionIds;
            var sub = eventsCanvas.createClick$(display).subscribe(function () {
                onBuyExpansionClick(item.id, expansionInfo.expansions[unlockedExpansionIds.length]);
                utilSound.event(IdEventSoundCore.ButtonClick);
            });
            _this.clickSubscriptionsByExpansionId.set(item.id, sub);
            var _a = utilGrid.expansionIdToXY(item.id, _this.baseSetup), x = _a.x, y = _a.y;
            var pos = utilGrid.objectCenterOnScreenGlobal({
                id: item.id,
                x: x,
                xExtent: _this.baseSetup.expansionsSizeX,
                y: y,
                yExtent: _this.baseSetup.expansionsSizeY,
            });
            var mouseOverSubTimer;
            var dispatched;
            var mouseOverSub = eventsCanvas.createMouseOver$(display).subscribe(function () {
                if (props.buildingPlacement.action !== TypeBuildingAction.Move) {
                    _this.addHighlight(display);
                }
                clearTimeout(mouseOverSubTimer);
                mouseOverSubTimer = setTimeout(function () {
                    dispatched = true;
                    onMouseOverDispatch(IdTooltipType.Expansion + item.id, new PIXI.Point(pos.x, pos.y - 150), IdLoca.expansionDialog_title);
                }, IdTooltipsDelay.standardDelay);
                _this.shouldIsoSortUpdate = false;
            });
            _this.mouseOverSubscriptionsByExpansionId.set(item.id, mouseOverSub);
            var mouseOutSub = eventsCanvas.createMouseOut$(display).subscribe(function () {
                _this.removeHighlight(display);
                if (mouseOverSubTimer) {
                    clearTimeout(mouseOverSubTimer);
                }
                if (dispatched) {
                    dispatched = false;
                    onMouseOutDispatch(IdTooltipType.Expansion + item.id);
                }
                _this.shouldIsoSortUpdate = false;
            });
            _this.mouseOutSubscriptionsByExpansionId.set(item.id, mouseOutSub);
        };
        _this.addHighlight = function (display) {
            _this.changeFilters(display, [new OutlineFilter(2, 0xffffff)]);
        };
        _this.removeHighlight = function (display) {
            // tslint:disable-next-line:no-null-keyword
            _this.changeFilters(display, null);
        };
        _this.changeFilters = function (display, filters) {
            if (display !== undefined) {
                var cache = display.cacheAsBitmap;
                display.cacheAsBitmap = false;
                display.filters = filters;
                display.cacheAsBitmap = cache;
            }
        };
        _this.cleanupExpansion = function (child) {
            if (child == undefined) {
                return;
            }
            _this.cleanupChild(child);
            var id = parseInt(child.name.split(" - ")[1], 10);
            _this.lockedGroundById.delete(id);
            _this.unlockableGroundById.delete(id);
            _this.cleanupSubscriptions(id);
        };
        _this.cleanupSubscriptions = function (id) {
            var clickSub = _this.clickSubscriptionsByExpansionId.get(id);
            if (clickSub) {
                clickSub.unsubscribe();
                _this.clickSubscriptionsByExpansionId.delete(id);
            }
            var mouseOverSub = _this.mouseOverSubscriptionsByExpansionId.get(id);
            if (mouseOverSub) {
                mouseOverSub.unsubscribe();
                _this.mouseOverSubscriptionsByExpansionId.delete(id);
            }
            var mouseOutSub = _this.mouseOutSubscriptionsByExpansionId.get(id);
            if (mouseOutSub) {
                mouseOutSub.unsubscribe();
                _this.mouseOutSubscriptionsByExpansionId.delete(id);
            }
        };
        _this.drawObject = function (expansionInfo, baseSetup, objectMap, getGraphic) {
            if (objectMap.has(expansionInfo.id)) {
                return objectMap.get(expansionInfo.id);
            }
            var isDiscount = _this.props.activeExpansionDiscount !== undefined;
            var _a = utilGrid.expansionIdToXY(expansionInfo.id, baseSetup), x = _a.x, y = _a.y;
            var expansionsSizeX = baseSetup.expansionsSizeX, expansionsSizeY = baseSetup.expansionsSizeY;
            var gridOobj = {
                id: expansionInfo.id,
                x: x,
                xExtent: expansionsSizeX,
                y: y,
                yExtent: expansionsSizeY,
            };
            var display = getGraphic(gridOobj, baseSetup, isDiscount);
            if (display != undefined) {
                var pos = utilGrid.mapToScreen(x, y);
                display.position.set(pos.x, pos.y);
                // Do not modify the name. Cleanup function depends on it
                display.name = "Expansion(" + x + ", " + y + ") - " + expansionInfo.id;
                _this.addChild(display);
                objectMap.set(expansionInfo.id, display);
            }
            return display;
        };
        return _this;
    }
    return LayerPlayerBaseLockedGround;
}(SceneLayer));
export { LayerPlayerBaseLockedGround };



//////////////////
// WEBPACK FOOTER
// ./src/features/player-base/layers/LayerPlayerBaseLockedGround.ts
// module id = 2219
// module chunks = 3